import { pipe, Either, JSON, Runtime } from "fp";

import * as Moment from "moment";
import React from "react";

import { English } from "~/English";
import { Search } from "~/Search";
import { Time } from "~/Time";

/** Duration in milliseconds */
export type Duration = { readonly duration: number };
export function Duration(duration: Duration) {
  return <>{Duration.toEnglish(duration)}</>;
}

export namespace Duration {
  export const milliseconds = (milliseconds: number): Duration => ({
    duration: milliseconds,
  });

  export const seconds = (seconds: number): Duration =>
    milliseconds(seconds * 1000);

  export const minutes = (minutes: number): Duration => seconds(minutes * 60);
  export const hours = (hours: number): Duration => minutes(hours * 60);
  export const days = (days: number): Duration => hours(days * 24);

  export const fromString = (string: string): Either<Error, Duration> =>
    pipe(
      Moment.duration(string),
      Either.fromPredicate(
        (moment) => moment.isValid(),
        () => error(string)
      ),
      Either.map((moment) => ({ duration: moment.asMilliseconds() }))
    );

  const error = (string: string): Error =>
    pipe(JSON.Stringify.Always.pretty(string), (json) =>
      Error(`Couldn't convert \`${json}\` into a \`Duration\``)
    );

  const is = (duration?: unknown): duration is Duration =>
    typeof duration === "number" &&
    !isNaN(duration) &&
    Moment.duration(duration, "milliseconds").isValid();

  export const toSearch: Search.From<Duration> = (duration) =>
    Search.join(
      Search.string(toEnglish(duration)),
      Search.string(toHHMM(duration)),
      Search.string(toHMMSS(duration))
    );

  export const toDays = ({ duration }: Duration): number =>
    Moment.duration(duration).asDays();

  export const toHours = ({ duration }: Duration): number =>
    Moment.duration(duration).asHours();

  export const toISO = ({ duration }: Duration): string =>
    Moment.duration(duration).toISOString();

  export const toEnglishHours = ({ duration }: Time.Duration): English =>
    English.pluralize("hour")(Math.floor(Moment.duration(duration).asHours()));

  export const toEnglish = (duration: Duration): English =>
    pipe(
      momentFromDuration(duration),
      (moment) =>
        pipe(Math.floor(moment.asDays()), (days) => ({
          days,
          hours: Math.floor(days > 0 ? moment.hours() : moment.asHours()),
          minutes: Math.floor(moment.minutes()),
          seconds: Math.floor(moment.seconds()),
        })),
      ({ days, hours, minutes, seconds }) => ({
        days: days > 0 ? English.pluralize("day")(days) : undefined,
        hours: hours > 0 ? English.pluralize("hour")(hours) : undefined,
        minutes: minutes > 0 ? English.pluralize("minute")(minutes) : undefined,
        seconds: seconds > 0 ? English.pluralize("second")(seconds) : undefined,
      }),
      ({ days, hours, minutes, seconds }) =>
        [days, hours, minutes, seconds].filter(
          (word): word is string => word !== undefined
        ),
      (words) => English.list(...words) ?? "Instant"
    );

  export const toHHMM = (duration: Duration): string =>
    pipe(
      momentFromDuration(duration),
      (moment) => ({
        hours: Math.floor(moment.asMilliseconds() / hours(1).duration),
        minutes: moment.minutes(),
      }),
      ({ hours, minutes }) => `${addPadding(hours)}:${addPadding(minutes)}`
    );

  export const toHMMSS = (duration: Duration): string =>
    pipe(momentFromDuration(duration), (moment) =>
      [
        moment.hours(),
        addPadding(moment.minutes()),
        addPadding(moment.seconds()),
      ].join(":")
    );

  export const to24HourHHMMSS = (duration: Duration): string =>
    pipe(momentFromDuration(duration), (moment) =>
      [
        addPadding(moment.hours()),
        addPadding(moment.minutes()),
        addPadding(moment.seconds()),
      ].join(":")
    );

  const addPadding = (timePart: number): string =>
    `${timePart}`.padStart(2, "0");

  const momentFromDuration = ({ duration }: Duration): Moment.Duration =>
    Moment.duration(duration, "milliseconds");

  export namespace Response {
    export const runtime = () =>
      new Runtime.Type(
        "DateTime",
        is,
        (input, context) =>
          pipe(
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            fromString(`${input}`),
            Either.map(Runtime.success),
            Either.getOrElse(() => Runtime.failure(input, context))
          ),
        Runtime.identity
      );
  }
}
